import React, { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { loadBuildingsForSelector } from '../../api';
import type { Building } from '../../api/buildingsApi';
import { notificationsApi, AppNotification } from '../../api/notificationsApi';
import { NotificationListItem } from '../../components/notifications/NotificationListItem';
import { NotificationOverlayHost } from '../../components/notifications/NotificationModals';
import { useNotificationActions } from '../../hooks/useNotificationActions';
import { useNotifications } from '../../contexts/NotificationsContext';

type ReadFilter = 'all' | 'unread' | 'read';

const PAGE_SIZE = 20;

const NotificationsPage: React.FC = () => {
  const { unreadCount, totalCount, refreshCounts } = useNotifications();
  const { overlay, closeOverlay, openNotification, markAllRead, deleteNotification } =
    useNotificationActions();

  const [buildings, setBuildings] = useState<Building[]>([]);
  const [buildingId, setBuildingId] = useState('');
  const [readFilter, setReadFilter] = useState<ReadFilter>('all');
  const [items, setItems] = useState<AppNotification[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    loadBuildingsForSelector().then(setBuildings).catch(() => {});
  }, []);

  const isReadParam = readFilter === 'all' ? undefined : readFilter === 'read';

  const fetchNotifications = useCallback(
    async (offset: number, append: boolean) => {
      if (append) {
        setLoadingMore(true);
      } else {
        setLoading(true);
        setHasMore(true);
      }
      try {
        const data = await notificationsApi.list({
          buildingId: buildingId || undefined,
          isRead: isReadParam,
          limit: PAGE_SIZE,
          offset,
        });
        setItems((prev) => (append ? [...prev, ...data] : data));
        setHasMore(data.length === PAGE_SIZE);
        await refreshCounts();
      } catch {
        toast.error('Không thể tải thông báo');
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [buildingId, isReadParam, refreshCounts],
  );

  useEffect(() => {
    setSelectedIds(new Set());
    setSelectionMode(false);
    fetchNotifications(0, false);
  }, [fetchNotifications]);

  useEffect(() => {
    const onChanged = () => {
      window.setTimeout(() => fetchNotifications(0, false), 300);
    };
    window.addEventListener('ebms:notifications-changed', onChanged);
    return () => window.removeEventListener('ebms:notifications-changed', onChanged);
  }, [fetchNotifications]);

  const handleDeleteOne = async (notification: AppNotification) => {
    if (!window.confirm('Xóa thông báo này?')) return;
    try {
      await deleteNotification(notification);
      setItems((prev) => prev.filter((n) => n.id !== notification.id));
    } catch {
      // toast in hook
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`Xóa ${selectedIds.size} thông báo đã chọn?`)) return;
    const toDelete = items.filter((n) => selectedIds.has(n.id));
    for (const n of toDelete) {
      try {
        await deleteNotification(n);
      } catch {
        break;
      }
    }
    setItems((prev) => prev.filter((n) => !selectedIds.has(n.id)));
    setSelectedIds(new Set());
    setSelectionMode(false);
  };

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const tabClass = (active: boolean) =>
    `px-4 py-2 text-sm font-medium rounded-lg ${
      active ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-100'
    }`;

  return (
    <div className="max-w-3xl mx-auto">
      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Thông báo</h1>
          <p className="text-sm text-gray-500 mt-1">
            {unreadCount} chưa đọc · {totalCount} tổng
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {unreadCount > 0 && (
            <button
              type="button"
              onClick={() =>
                markAllRead(buildingId || undefined).then(() => fetchNotifications(0, false))
              }
              className="px-3 py-2 text-sm font-medium text-blue-700 bg-blue-50 rounded-lg hover:bg-blue-100"
            >
              Đánh dấu đã đọc tất cả
            </button>
          )}
          <button
            type="button"
            onClick={() => {
              setSelectionMode((v) => !v);
              setSelectedIds(new Set());
            }}
            className="px-3 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            {selectionMode ? 'Hủy chọn' : 'Chọn để xóa'}
          </button>
          {selectionMode && selectedIds.size > 0 && (
            <button
              type="button"
              onClick={handleDeleteSelected}
              className="px-3 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700"
            >
              Xóa ({selectedIds.size})
            </button>
          )}
        </div>
      </div>

      <div className="flex flex-wrap gap-3 mb-4">
        {buildings.length > 1 && (
          <select
            value={buildingId}
            onChange={(e) => setBuildingId(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
          >
            <option value="">Tất cả tòa nhà</option>
            {buildings.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </select>
        )}
        <div className="flex gap-1 p-1 bg-gray-100 rounded-lg">
          <button type="button" className={tabClass(readFilter === 'all')} onClick={() => setReadFilter('all')}>
            Tất cả
          </button>
          <button
            type="button"
            className={tabClass(readFilter === 'unread')}
            onClick={() => setReadFilter('unread')}
          >
            Chưa đọc
          </button>
          <button type="button" className={tabClass(readFilter === 'read')} onClick={() => setReadFilter('read')}>
            Đã đọc
          </button>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        {loading && items.length === 0 ? (
          <p className="text-sm text-gray-500 text-center py-12">Đang tải…</p>
        ) : items.length === 0 ? (
          <p className="text-sm text-gray-500 text-center py-12">Không có thông báo</p>
        ) : (
          <>
            {items.map((n) => (
              <NotificationListItem
                key={n.id}
                notification={n}
                selectionMode={selectionMode}
                selected={selectedIds.has(n.id)}
                onToggleSelect={() => toggleSelect(n.id)}
                onClick={() =>
                  openNotification(n).then(() => {
                    setItems((prev) =>
                      prev.map((row) =>
                        row.id === n.id ? { ...row, isRead: true, readAt: new Date().toISOString() } : row,
                      ),
                    );
                  })
                }
                onDelete={() => handleDeleteOne(n)}
              />
            ))}
            {hasMore && (
              <div className="p-3 border-t border-gray-100 text-center">
                <button
                  type="button"
                  disabled={loadingMore}
                  onClick={() => fetchNotifications(items.length, true)}
                  className="text-sm font-medium text-blue-600 hover:text-blue-800 disabled:opacity-50"
                >
                  {loadingMore ? 'Đang tải…' : 'Tải thêm'}
                </button>
              </div>
            )}
          </>
        )}
      </div>

      <NotificationOverlayHost overlay={overlay} onClose={closeOverlay} />
    </div>
  );
};

export default NotificationsPage;
